import { createSlice } from '@reduxjs/toolkit';
import { fetchDentallyPractitioners } from '../../utils/dentallyApi';
import { hasFeatureAccess } from '../../utils/featurePermission';

const PRACTITIONER_REFRESH_ERROR = Object.freeze({
  code: 'practitioner_refresh_failed',
  message: 'Unable to refresh Dentally practitioners.',
  retryable: true,
});

let practitionerRequestSequence = 0;

const nextPractitionerRequestId = () => {
  practitionerRequestSequence += 1;
  return practitionerRequestSequence;
};

const isPlainObject = (value) => (
  value !== null
  && typeof value === 'object'
  && !Array.isArray(value)
  && Object.getPrototypeOf(value) === Object.prototype
);

// Read only the current versioned envelope for the requested practice.
function hydrateFromCache(practiceId) {
  try {
    const raw = localStorage.getItem(`dentallyPractitioners_${practiceId}`);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (
      !isPlainObject(cached)
      || String(cached.practiceId) !== String(practiceId)
      || !Array.isArray(cached.practitioners)
    ) {
      return null;
    }
    return cached.practitioners;
  } catch {
    // Invalid/disabled localStorage is a cache miss, never current data.
    return null;
  }
}

const initialState = {
  practitioners: [],
  isLoading: false,
  isLoaded: false,
  practiceId: null,
  requestId: null,
  source: null,
  error: null,
};

const slice = createSlice({
  name: 'dentallyPractitioners',
  initialState,
  reducers: {
    requestStarted(state, action) {
      const { practiceId, requestId, cached } = action.payload;
      const samePractice = String(state.practiceId) === String(practiceId);
      const hasCachedEnvelope = Array.isArray(cached);

      if (!samePractice || hasCachedEnvelope) {
        state.practitioners = hasCachedEnvelope ? cached : [];
        state.source = hasCachedEnvelope ? 'cache' : null;
      }
      state.isLoading = true;
      state.isLoaded = false;
      state.practiceId = practiceId;
      state.requestId = requestId;
      state.error = null;
    },
    requestSucceeded(state, action) {
      const {
        practiceId,
        requestId,
        practitioners,
      } = action.payload;
      if (state.practiceId !== practiceId || state.requestId !== requestId) return;

      state.practitioners = practitioners;
      state.isLoading = false;
      state.isLoaded = true;
      state.source = 'network';
      state.error = null;
    },
    requestFailed(state, action) {
      const { practiceId, requestId, error } = action.payload;
      if (state.practiceId !== practiceId || state.requestId !== requestId) return;

      state.isLoading = false;
      state.isLoaded = false;
      state.error = error;
    },
    reset(state) {
      state.practitioners = [];
      state.isLoading = false;
      state.isLoaded = false;
      state.practiceId = null;
      state.requestId = null;
      state.source = null;
      state.error = null;
    },
  },
});

export default slice.reducer;

export const { reset: resetDentallyPractitioners } = slice.actions;

// Prefetch all practitioners: hydrate matching local data, then validate it from the network.
export function prefetchDentallyPractitioners(practiceId) {
  return async (thunkDispatch, getState) => {
    if (!practiceId) return;

    // Skip unless dentally-integration is explicitly 'view' or 'manage'.
    if (!hasFeatureAccess('dentally-integration')) return;

    const normalizedPracticeId = String(practiceId);
    const requestId = nextPractitionerRequestId();
    const cached = hydrateFromCache(normalizedPracticeId);
    thunkDispatch(slice.actions.requestStarted({
      practiceId: normalizedPracticeId,
      requestId,
      cached,
    }));

    try {
      const practitioners = await fetchDentallyPractitioners(practiceId);
      if (!Array.isArray(practitioners)) throw new Error('Invalid practitioner collection');

      thunkDispatch(slice.actions.requestSucceeded({
        practiceId: normalizedPracticeId,
        requestId,
        practitioners,
      }));

      const currentState = typeof getState === 'function'
        ? getState()?.dentallyPractitioners
        : null;
      const completionIsCurrent = currentState?.practiceId === normalizedPracticeId
        && currentState?.requestId === requestId
        && currentState?.source === 'network';
      if (!completionIsCurrent) return;

      try {
        localStorage.setItem(
          `dentallyPractitioners_${normalizedPracticeId}`,
          JSON.stringify({
            practiceId: normalizedPracticeId,
            savedAt: new Date().toISOString(),
            practitioners,
          }),
        );
      } catch {
        // Cache persistence is optional; Redux still owns the accepted response.
      }
    } catch {
      thunkDispatch(slice.actions.requestFailed({
        practiceId: normalizedPracticeId,
        requestId,
        error: { ...PRACTITIONER_REFRESH_ERROR },
      }));
    }
  };
}
